// frontend/src/components/planner/AiAssistantPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { TokenStorage } from '../../utils/api';
import AppShell from '../layout/AppShell';

// ---------------------------------------------------------------------------
// Tool-call display name map
// ---------------------------------------------------------------------------
const formatToolName = (name) => {
  const map = {
    get_account_balances: 'Looking up balances...',
    search_transactions: 'Searching transactions...',
    get_spending_by_category: 'Analyzing spending...',
    get_spending_by_month: 'Analyzing monthly spending...',
    get_upcoming_bills: 'Checking upcoming bills...',
    get_debt_summary: 'Reviewing debts...',
    get_recurring_items: 'Loading recurring items...',
    get_forecast_summary: 'Generating forecast...',
  };
  return map[name] || 'Working...';
};

// ---------------------------------------------------------------------------
// Simple inline markdown renderer (no external deps)
// Handles: headings, bold, italic, inline code, fenced code blocks,
//          unordered list items, and basic markdown tables.
// ---------------------------------------------------------------------------
function renderMarkdown(text) {
  if (!text) return '';

  // Escape HTML entities first to prevent XSS
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Fenced code blocks (``` ... ```)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, _lang, code) => {
    return `<pre class="bg-gray-800 text-gray-100 rounded-lg p-3 my-2 overflow-x-auto text-sm leading-relaxed"><code>${code.trim()}</code></pre>`;
  });

  // Split into lines for block-level processing
  const lines = html.split('\n');
  const processed = [];
  let inTable = false;
  let tableRows = [];

  const flushTable = () => {
    if (tableRows.length === 0) return;
    let tableHtml = '<div class="overflow-x-auto my-2"><table class="min-w-full text-sm border border-gray-200 rounded">';
    tableRows.forEach((row, idx) => {
      // Skip separator rows (e.g. |---|---|)
      if (/^\|[\s-:|]+\|$/.test(row.trim())) return;
      const cells = row.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1);
      const tag = idx === 0 ? 'th' : 'td';
      const cellClass = idx === 0
        ? 'px-3 py-1.5 text-left font-semibold bg-gray-50 border-b border-gray-200'
        : 'px-3 py-1.5 border-b border-gray-100';
      tableHtml += '<tr>';
      cells.forEach((cell) => {
        tableHtml += `<${tag} class="${cellClass}">${cell.trim()}</${tag}>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</table></div>';
    processed.push(tableHtml);
    tableRows = [];
    inTable = false;
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Table rows start and end with |
    if (/^\|.*\|$/.test(trimmed)) {
      inTable = true;
      tableRows.push(trimmed);
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Skip if inside a <pre> block (already rendered)
    if (trimmed.startsWith('<pre')) {
      processed.push(line);
      continue;
    }

    // Headings
    if (/^### (.+)$/.test(trimmed)) {
      processed.push(`<h3 class="text-base font-bold mt-3 mb-1">${trimmed.slice(4)}</h3>`);
      continue;
    }
    if (/^## (.+)$/.test(trimmed)) {
      processed.push(`<h2 class="text-lg font-bold mt-3 mb-1">${trimmed.slice(3)}</h2>`);
      continue;
    }
    if (/^# (.+)$/.test(trimmed)) {
      processed.push(`<h1 class="text-xl font-bold mt-3 mb-1">${trimmed.slice(2)}</h1>`);
      continue;
    }

    // Unordered list items
    if (/^[-*] (.+)$/.test(trimmed)) {
      const content = trimmed.replace(/^[-*] /, '');
      processed.push(`<li class="ml-4 list-disc">${content}</li>`);
      continue;
    }

    // Empty line
    if (trimmed === '') {
      processed.push('<br/>');
      continue;
    }

    // Default: paragraph-ish line
    processed.push(`<p class="my-0.5">${line}</p>`);
  }
  // Flush any remaining table
  flushTable();

  html = processed.join('\n');

  // Inline styles (applied after block processing)
  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded text-sm">$1</code>');
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic (single *)
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  return html;
}

// ---------------------------------------------------------------------------
// Suggestion chips shown when conversation is empty
// ---------------------------------------------------------------------------
const SUGGESTIONS = [
  'What are my account balances?',
  'How much did I spend on food this month?',
  'Show me my upcoming bills',
  "What's my biggest expense category?",
  'Am I spending more than I earn?',
];

// ---------------------------------------------------------------------------
// Pulsing dots loader
// ---------------------------------------------------------------------------
function PulsingDots() {
  return (
    <span className="inline-flex items-center gap-1" aria-label="AI is thinking">
      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '200ms' }} />
      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '400ms' }} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function AiAssistantPanel() {
  // Chat state
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content: string }
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState(null);
  const [error, setError] = useState(null);

  // Session sidebar state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);
  // Accumulator ref so the SSE loop always sees the latest partial content
  const assistantContentRef = useRef('');

  // ------- Auto-scroll to bottom on new messages -------
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, toolStatus, streaming, scrollToBottom]);

  // ------- Load sessions on mount -------
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await api.get('/api/ai/sessions');
      setSessions(res.data.sessions || res.data || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadSession = async (id) => {
    try {
      const res = await api.get(`/api/ai/sessions/${id}`);
      const session = res.data.session || res.data;
      setSessionId(id);
      setMessages(
        (session.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        }))
      );
      setError(null);
      setToolStatus(null);
      setSidebarOpen(false);
    } catch (err) {
      console.error('Failed to load session:', err);
      setError('Failed to load conversation.');
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/api/ai/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (sessionId === id) {
        setSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setError(null);
    setToolStatus(null);
    setSidebarOpen(false);
    textareaRef.current?.focus();
  };

  // ------- Send message via SSE -------
  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || streaming) return;

    const userMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setStreaming(true);
    setError(null);
    setToolStatus(null);
    assistantContentRef.current = '';

    // Add a placeholder assistant message that we will update in-place
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = TokenStorage.getToken();
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: trimmed, sessionId }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || `Server returned ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop(); // keep the incomplete chunk

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          let data;
          try {
            data = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          switch (data.type) {
            case 'session':
              setSessionId(data.sessionId);
              break;

            case 'tool_call':
              setToolStatus(formatToolName(data.name));
              break;

            case 'text':
              assistantContentRef.current += data.content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: 'assistant',
                  content: assistantContentRef.current,
                };
                return updated;
              });
              break;

            case 'error':
              setError(data.message);
              break;

            case 'done':
              setToolStatus(null);
              break;

            default:
              break;
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Chat SSE error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      // Remove the empty assistant placeholder if nothing was streamed
      setMessages((prev) => {
        if (prev.length > 0 && prev[prev.length - 1].role === 'assistant' && !prev[prev.length - 1].content) {
          return prev.slice(0, -1);
        }
        return prev;
      });
    } finally {
      setStreaming(false);
      setToolStatus(null);
      abortRef.current = null;
      // Refresh session list so the new / updated session appears
      loadSessions();
    }
  };

  const handleRetry = () => {
    if (messages.length === 0) return;
    // Find the last user message
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMsg) return;
    // Remove the error assistant placeholder if present
    setMessages((prev) => {
      const copy = [...prev];
      while (copy.length > 0 && copy[copy.length - 1].role === 'assistant') {
        copy.pop();
      }
      return copy;
    });
    setError(null);
    sendMessage(lastUserMsg.content);
  };

  // ------- Textarea auto-grow -------
  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    // Reset height to auto to shrink on delete, then expand to scrollHeight
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ------- Render -------
  const hasMessages = messages.length > 0;

  return (
    <AppShell>
      <div className="flex h-[calc(100vh-10rem)] lg:h-[calc(100vh-8rem)] -mx-4 sm:-mx-6 lg:-mx-8 -my-4 sm:-my-6 lg:-my-8">

        {/* ============ Mobile sidebar overlay ============ */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ============ Sidebar ============ */}
        <aside
          className={`
            fixed lg:static inset-y-0 left-0 z-40
            w-64 bg-gray-50 border-r border-gray-200 flex flex-col
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {/* New Chat button */}
          <div className="p-3 border-b border-gray-200">
            <button
              onClick={startNewChat}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-base sm:text-sm font-medium hover:bg-emerald-700 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 min-h-[44px]"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              New Chat
            </button>
          </div>

          {/* Session list */}
          <div className="flex-1 overflow-y-auto">
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <PulsingDots />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-base sm:text-sm text-gray-400 text-center py-8 px-4">
                No conversations yet.
              </p>
            ) : (
              <ul className="py-2">
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    onClick={() => loadSession(s.id)}
                    className={`group flex items-center gap-2 px-3 py-2.5 mx-2 rounded-lg cursor-pointer transition-colors min-h-[44px] ${
                      sessionId === s.id
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {/* Chat icon */}
                    <svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>

                    <span className="text-base sm:text-sm truncate flex-1">
                      {s.title || 'Untitled chat'}
                    </span>

                    {/* Delete button */}
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-all focus:outline-none min-w-[44px] min-h-[44px] sm:min-h-0 sm:min-w-[28px] flex items-center justify-center"
                      aria-label={`Delete ${s.title || 'chat'}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* ============ Chat area ============ */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {/* Chat header (mobile has sidebar toggle) */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
            {/* Mobile sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-600 focus:outline-none min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Open chat history"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </button>

            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-sm font-semibold text-gray-900 truncate">
                AI Assistant
              </h1>
              <p className="text-xs text-gray-400 truncate hidden sm:block">
                Ask about your finances, spending, and forecasts
              </p>
            </div>

            {/* New chat shortcut (mobile) */}
            <button
              onClick={startNewChat}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600 focus:outline-none min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="New chat"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {/* Empty state with suggestions */}
            {!hasMessages && !streaming && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  How can I help?
                </h2>
                <p className="text-base sm:text-sm text-gray-500 mb-6 max-w-sm">
                  Ask me anything about your finances. I can look up balances, search transactions, and analyze your spending.
                </p>
                <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => sendMessage(suggestion)}
                      className="px-4 py-2.5 rounded-full border border-gray-200 bg-white text-base sm:text-sm text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 min-h-[44px]"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat messages */}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-2 text-base sm:text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    msg.content ? (
                      <div
                        className="prose prose-sm max-w-none [&_pre]:my-2 [&_li]:my-0 [&_p]:my-0.5"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                      />
                    ) : (
                      // Still streaming, no content yet
                      streaming && idx === messages.length - 1 ? (
                        <PulsingDots />
                      ) : null
                    )
                  ) : (
                    <span className="whitespace-pre-wrap">{msg.content}</span>
                  )}
                </div>
              </div>
            ))}

            {/* Tool status indicator */}
            {toolStatus && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl px-4 py-2 bg-gray-100 text-gray-600 text-base sm:text-sm">
                  <svg className="w-4 h-4 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  {toolStatus}
                </div>
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="flex justify-start">
                <div className="max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-base sm:text-sm">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="break-words">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="mt-2 text-sm font-medium text-red-600 hover:text-red-800 underline underline-offset-2 focus:outline-none min-h-[44px] sm:min-h-0"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div className="border-t border-gray-200 bg-white px-4 py-3">
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your finances..."
                rows={1}
                disabled={streaming}
                className="flex-1 resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-base sm:text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] max-h-[200px]"
              />
              <button
                onClick={() => sendMessage()}
                disabled={streaming || !input.trim()}
                className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 min-h-[44px] sm:min-h-0"
                aria-label="Send message"
              >
                {streaming ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-2 hidden sm:block">
              Press Enter to send, Shift+Enter for a new line
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
